import { RouterModule, Routes } from "@angular/router";
import { AdminGuard } from "./_guards/admin.guard";
import { AdminOrSuperAdminGuard } from "./_guards/adminOrSuperAdmin.guard";
import { AuthGuard } from "src/app/_guards";
import { BrowserUtils } from "@azure/msal-browser";
import { CanAccessPageGuard } from "./_guards/canAccessPage.guard";
import { EntityPermissionGuard } from "./_guards/entityPermission.guard";
import {
    PermissionRedirectGuard,
    PermissionRedirectPlaceholderComponent,
} from "./_guards/permissionRedirect.guard";
import { DataAnalysisPageComponent } from "./components/dataAnalysis/dataAnalysisPage.component";
import { DeviceDetailComponent } from "./components/device-detail/device-detail.component";
import { DeviceVersionListComponent } from "./components/deviceVersionList/deviceVersionList.component";
import { ForgotPasswordComponent } from "./components/forgot-password/forgot-password.component";
import { HomeComponent } from "./components/home/home.component";
import { LoginComponent } from "./components/login/login.component";
import { NgModule } from "@angular/core";
import { NodeDetailComponent } from "./components/node-detail/node-detail.component";
import { NotificationListComponent } from "./components/notification-list/notification-list.component";
import { NotificationSettingsComponent } from "./components/notification-settings/notification-settings.component";
import { ParametersListComponent } from "./components/parameters-list/parameters-list.component";
import { OrganizationLevelPermissions } from "./models/organizationLevelPermissions";
import { RolePermissionObjects } from "./shared/constants/rolePermissionObjects.constants";
import { PreSignupUserListComponent } from "./components/preSignup/preSignupUserList.component";
import { RealTimeMonitoringDashboardComponent } from "./components/real-time-monitoring-dashboard/real-time-monitoring-dashboard.component";
import { ReportListComponent } from "./components/report-list/report-list.component";
import { ReportViewComponent } from "./components/report-view/report-view.component";
import { ReportsComponent } from "./components/reports/reports.component";
import { ResetPasswordComponent } from "./components/reset-password/reset-password.component";
import { SampleResultComponent } from "./components/sample-result/sample-result.component";
import { SampleListComponent } from "./components/sample-list/sample-list.component";
import { SegListComponent } from "./components/seg-list/seg-list.component";
import { ShadowConstraintListComponent } from "./components/shadowConstraintList/shadowConstraintList.component";
import { SignupComponent } from "./components/signup/signup.component";
import { SuperAdminGuard } from "./_guards/superAdmin.guard";
import { TenantListComponent } from "./components/tenantList/tenantList.component";
import { TenantSensorListComponent } from "./components/tenantSensorList/tenantSensorList.component";
import { UnauthorizedComponent } from "./common-components/unauthorized/unauthorized.component";
import { UserListComponent } from "./components/user-list/user-list.component";
import { ZonesComponent } from "./components/zones/zones.component";
import nameOf from "./shared/form/nameOf.helper";
import { SensorComponent } from "./components/sensor/sensor.component";
import { LocationListComponent } from "./components/location-list/location-list.component";
import { TaskListComponent } from "./components/task-list/task-list.component";
import { PpeListComponent } from "./components/ppe-list/ppe-list.component";
import { ControlsListComponent } from "./components/controls-list/controls-list.component";
import { RolePermissionsTabComponent } from "./components/role-permissions-tab/role-permissions-tab.component";
import { RiskAssessmentComponent } from "./components/risk-assessment/risk-assessment.component";
import { RiskRegisterComponent } from "./components/risk-register/risk-register.component";
import { AgentsListComponent } from "./components/agents-list/agents-list.component";
import { JurisdictionsListComponent } from "./components/jurisdictions-list/jurisdictions-list.component";
import { EndpointsComponent } from "./components/endpoints/endpoints.component";
import { ShiftListComponent } from "./components/shift-list/shift-list.component";
import { DevicesTableComponent } from "./components/devices-table/devices-table.component";
import { SurveyListComponent } from "./components/survey-list/survey-list.component";
import { AgentGroupListComponent } from "./components/agent-group-list/agent-group-list.component";
import { CalibrationEquipmentComponent } from "./components/calibration-equipment/calibration-equipment.component";
import { SamplingEquipmentComponent } from "./components/sampling-equipment/sampling-equipment.component";
import { AnalyticalMethodComponent } from "./components/analytical-method/analytical-method.component";
import { SampleMediaComponent } from "./components/sample-media/sample-media.component";
import { SampleDetailsComponent } from "./components/sample-details/sample-details.component";
import { OrganizationsComponent } from "./components/organizations/organizations.component";
import { SampleHistoryComponent } from "./components/sample-history/sample-history.component";
import { SampleSurveyDetailsComponent } from "./components/sample-survey-details/sample-survey-details.component";
import { OelTypeComponent } from "./components/oel-type/oel-type.component";
import { JobDictionaryComponent } from "./components/job-dictionary/job-dictionary.component";
import { JobMappingComponent } from "./components/job-mapping/job-mapping.component";
import { LabIntegrationsComponent } from "./components/lab-integrations/lab-integrations.component";
import { LabRequisitionListComponent } from "./components/lab-requisition-list/lab-requisition-list.component";
import { FileAttachmentListComponent } from "./components/file-attachment-list/file-attachment-list.component";
import { AccessLogListComponent } from "./components/access-log-list/access-log-list.component";
import { CalibrationDetailsComponent } from "./components/calibration-details/calibration-details.component";
import { CalibrationMaintenanceHistoryComponent } from "./components/calibration-maintenance-history/calibration-maintenance-history.component";
import { LabIntegrationDetailsComponent } from "./components/lab-integration-details/lab-integration-details.component";
import { DashboardComponent } from "./components/dashboard/dashboard.component";
import { TemplateListHostComponent } from "./components/template-list-host/template-list-host.component";
import { SamplePlanningListComponent } from "./components/sample-planning-list/sample-planning-list.component";
import { SamplePlanningDetailComponent } from "./components/sample-planning-detail/sample-planning-detail.component";
import { ReportTemplateListComponent } from "./components/report-template-list/report-template-list.component";
import { AiAnalystComponent } from "./components/ai-analyst/ai-analyst.component";
import { aiAnalystRouteMatcher } from "./shared/utils/ai-analyst-route.util";
import { AiAnalystAdminComponent } from "./components/ai-analyst-admin/ai-analyst-admin.component";
import { SiteCreditBalanceListComponent } from "./components/site-credit-balance-list/site-credit-balance-list.component";
import { CorporateReportingComponent } from "./components/corporate-reporting/corporate-reporting.component";
import { CorporateReportingGuard } from "./_guards/corporateReporting.guard";
import { AiAnalystGuard } from "./_guards/aiAnalyst.guard";
import { AiAnalystAccessGuard } from "./_guards/aiAnalystAccess.guard";
import { AiUsageByUserListComponent } from "./components/ai-usage-by-user/ai-usage-by-user-list.component";
import { AlertResponseComponent } from "./components/alert-response/alert-response.component";
import { ACK_LINK_ROUTE_SEGMENT } from "./shared/constants/alertResponse.constants";
import { AlertResponsePreviewComponent } from "./components/alert-response-preview/alert-response-preview.component";

export const appRoutes: Routes = [
    { path: "", redirectTo: "/login", pathMatch: "full" },
    { path: "login", component: LoginComponent },
    { path: "sign-up", component: SignupComponent },
    { path: "cancel", redirectTo: "/login", pathMatch: "full" },
    { path: "forgotpassword", component: ForgotPasswordComponent },
    { path: "updateuserpassword/:id", component: ResetPasswordComponent },
    { path: "reports/:id", component: ReportsComponent },
    { path: "alert-response/:token", component: AlertResponseComponent },
    // Email and SMS acknowledgement links are built by the API as {ApplicationUrl}/ack/{token}.
    { path: `${ACK_LINK_ROUTE_SEGMENT}/:token`, component: AlertResponseComponent },
    { path: "alert-response-preview", component: AlertResponsePreviewComponent },
    {
        path: "dashboard",
        component: HomeComponent,
        canActivate: [AuthGuard],
        children: [
            {
                path: "v2/:path",
                redirectTo: ":path",
                pathMatch: "prefix",
            },
            {
                path: "my-dashboard",
                component: DashboardComponent,
                canActivate: [CanAccessPageGuard],
                data: {
                    requiredPermission: nameOf<OrganizationLevelPermissions>(
                        (x) => x.canAccessIhModule,
                    ),
                },
            },
            {
                path: "corporate-reporting",
                component: CorporateReportingComponent,
                canActivate: [CorporateReportingGuard],
            },
            {
                path: "sample-details/:id",
                component: SampleDetailsComponent,
                canActivate: [CanAccessPageGuard],
                data: {
                    requiredPermission: nameOf<OrganizationLevelPermissions>(
                        (x) => x.canAccessIhModule,
                    ),
                },
            },
            {
                path: "sample-result/:id",
                component: SampleResultComponent,
                canActivate: [CanAccessPageGuard],
                data: {
                    requiredPermission: nameOf<OrganizationLevelPermissions>(
                        (x) => x.canAccessIhModule,
                    ),
                },
            },
            {
                path: "sample-history/:id",
                component: SampleHistoryComponent,
                canActivate: [CanAccessPageGuard],
                data: {
                    requiredPermission: nameOf<OrganizationLevelPermissions>(
                        (x) => x.canAccessIhModule,
                    ),
                },
            },
            {
                path: "samples",
                canActivate: [CanAccessPageGuard],
                data: {
                    requiredPermission: nameOf<OrganizationLevelPermissions>(
                        (x) => x.canAccessIhModule,
                    ),
                },
                children: [
                    {
                        path: "",
                        canActivate: [PermissionRedirectGuard],
                        component: PermissionRedirectPlaceholderComponent,
                    },
                    {
                        path: "sample",
                        component: SampleListComponent,
                        canActivate: [EntityPermissionGuard],
                        data: { objectId: RolePermissionObjects.Sample },
                    },
                    {
                        path: "survey",
                        component: SurveyListComponent,
                        canActivate: [EntityPermissionGuard],
                        data: { objectId: RolePermissionObjects.Survey },
                    },
                    {
                        path: "lab-requisition",
                        component: LabRequisitionListComponent,
                        canActivate: [EntityPermissionGuard],
                        data: {
                            objectId: RolePermissionObjects.LabRequisition,
                        },
                    },
                    {
                        path: "templates",
                        component: TemplateListHostComponent,
                        canActivate: [EntityPermissionGuard],
                        data: {
                            objectId: RolePermissionObjects.SampleTemplate,
                        },
                    },
                ],
            },
            {
                // DASH-2718 (item 6): the plan-detail page moved under Risk Assessment. Keep a stub for the former
                // top-level deep link, preserving :id so a bookmarked/emailed plan link still opens that plan.
                path: "planning/:id",
                redirectTo: "risk-assessments/planning/:id",
                pathMatch: "full",
            },
            {
                path: "survey-details/:id",
                component: SampleSurveyDetailsComponent,
                canActivate: [CanAccessPageGuard],
                data: {
                    requiredPermission: nameOf<OrganizationLevelPermissions>(
                        (x) => x.canAccessIhModule,
                    ),
                },
            },
            {
                path: "segs",
                canActivate: [CanAccessPageGuard],
                data: {
                    requiredPermission: nameOf<OrganizationLevelPermissions>(
                        (x) => x.canAccessIhModule,
                    ),
                },
                children: [
                    {
                        path: "",
                        canActivate: [PermissionRedirectGuard],
                        component: PermissionRedirectPlaceholderComponent,
                    },
                    {
                        path: "seg",
                        component: SegListComponent,
                        canActivate: [EntityPermissionGuard],
                        data: { objectId: RolePermissionObjects.SEG },
                    },
                    {
                        path: "agent",
                        component: AgentsListComponent,
                        canActivate: [EntityPermissionGuard],
                        data: { objectId: RolePermissionObjects.Agent },
                    },
                    {
                        path: "agent-group",
                        component: AgentGroupListComponent,
                        canActivate: [EntityPermissionGuard],
                        data: { objectId: RolePermissionObjects.AgentGroup },
                    },
                    {
                        path: "jurisdiction",
                        component: JurisdictionsListComponent,
                        canActivate: [EntityPermissionGuard],
                        data: { objectId: RolePermissionObjects.Jurisdiction },
                    },
                    {
                        path: "task",
                        component: TaskListComponent,
                        canActivate: [EntityPermissionGuard],
                        data: { objectId: RolePermissionObjects.TaskGroup },
                    },
                    {
                        path: "location",
                        component: LocationListComponent,
                        canActivate: [EntityPermissionGuard],
                        data: { objectId: RolePermissionObjects.LocationGroup },
                    },
                    {
                        path: "ppe",
                        component: PpeListComponent,
                        canActivate: [EntityPermissionGuard],
                        data: { objectId: RolePermissionObjects.Ppe },
                    },
                    {
                        path: "analytical-method",
                        component: AnalyticalMethodComponent,
                        canActivate: [EntityPermissionGuard],
                        data: {
                            objectId: RolePermissionObjects.AnalyticalMethod,
                        },
                    },
                    {
                        path: "sample-media",
                        component: SampleMediaComponent,
                        canActivate: [EntityPermissionGuard],
                        data: { objectId: RolePermissionObjects.SampleMedia },
                    },
                    {
                        path: "oel-type",
                        component: OelTypeComponent,
                        canActivate: [EntityPermissionGuard],
                        data: { objectId: RolePermissionObjects.OelType },
                    },
                    {
                        path: "controls",
                        component: ControlsListComponent,
                        canActivate: [EntityPermissionGuard],
                        data: { objectId: RolePermissionObjects.Control },
                    },
                    {
                        path: "report-template",
                        component: ReportTemplateListComponent,
                        canActivate: [EntityPermissionGuard],
                        data: { objectId: RolePermissionObjects.ReportTemplate },
                    },
                    {
                        // DASH-2718 (item 6): Planning moved under Risk Assessment. Keep a stub so old
                        // bookmarks / emailed links to the former list URL don't fall through to "**" -> /dashboard.
                        path: "planning",
                        redirectTo: "/dashboard/risk-assessments/planning",
                        pathMatch: "full",
                    },
                    {
                        path: "job-dictionary",
                        redirectTo: "/dashboard/workforce/job-dictionary",
                        pathMatch: "full",
                    },
                    {
                        path: "job-mapping",
                        redirectTo: "/dashboard/workforce/job-mapping",
                        pathMatch: "full",
                    },
                ],
            },
            {
                path: "risk-assessments",
                canActivate: [CanAccessPageGuard],
                data: {
                    requiredPermission: nameOf<OrganizationLevelPermissions>(
                        (x) => x.canAccessIhModule,
                    ),
                },
                children: [
                    { path: "", redirectTo: "assessments", pathMatch: "full" },
                    { path: "assessments", component: RiskAssessmentComponent },
                    { path: "controls", component: RiskAssessmentComponent },
                    {
                        path: "planning",
                        component: SamplePlanningListComponent,
                        canActivate: [EntityPermissionGuard],
                        data: {
                            objectId: RolePermissionObjects.SamplingPlan,
                        },
                    },
                    {
                        path: "planning/:id",
                        component: SamplePlanningDetailComponent,
                        canActivate: [EntityPermissionGuard],
                        data: {
                            objectId: RolePermissionObjects.SamplingPlan,
                        },
                    },
                ],
            },
            {
                path: "risk-assessment/:id",
                component: RiskRegisterComponent,
                canActivate: [CanAccessPageGuard],
                data: {
                    requiredPermission: nameOf<OrganizationLevelPermissions>(
                        (x) => x.canAccessIhModule,
                    ),
                },
            },
            {
                path: "devices",
                children: [
                    { path: "", redirectTo: "device", pathMatch: "full" },
                    {
                        path: "device",
                        component: DevicesTableComponent,
                        canActivate: [EntityPermissionGuard],
                        data: { objectId: RolePermissionObjects.Sensor },
                    },
                    {
                        path: "calibration-equipment",
                        component: CalibrationEquipmentComponent,
                        canActivate: [
                            CanAccessPageGuard,
                            EntityPermissionGuard,
                        ],
                        data: {
                            requiredPermission:
                                nameOf<OrganizationLevelPermissions>(
                                    (x) => x.canAccessIhModule,
                                ),
                            objectId:
                                RolePermissionObjects.CalibrationEquipment,
                        },
                    },
                    {
                        path: "sampling-equipment",
                        component: SamplingEquipmentComponent,
                        canActivate: [
                            CanAccessPageGuard,
                            EntityPermissionGuard,
                        ],
                        data: {
                            requiredPermission:
                                nameOf<OrganizationLevelPermissions>(
                                    (x) => x.canAccessIhModule,
                                ),
                            objectId: RolePermissionObjects.SamplingEquipment,
                        },
                    },
                ],
            },
            {
                path: "calibration-details/:id",
                component: CalibrationDetailsComponent,
                canActivate: [CanAccessPageGuard],
                data: {
                    requiredPermission: nameOf<OrganizationLevelPermissions>(
                        (x) => x.canAccessIhModule,
                    ),
                },
            },
            {
                path: "calibration-history/:id",
                component: CalibrationMaintenanceHistoryComponent,
                canActivate: [CanAccessPageGuard],
                data: {
                    requiredPermission: nameOf<OrganizationLevelPermissions>(
                        (x) => x.canAccessIhModule,
                    ),
                },
            },
            {
                path: "nodes/:coId",
                component: EndpointsComponent,
            },
            {
                path: "device-detail",
                component: DeviceDetailComponent,
            },
            {
                path: "node-detail/:id",
                component: NodeDetailComponent,
                canActivate: [CanAccessPageGuard],
                data: {
                    requiredPermission: nameOf<OrganizationLevelPermissions>(
                        (x) => x.canAccessRealTimeDashboard,
                    ),
                },
            },
            {
                path: "real-time-monitoring-dashboard",
                component: RealTimeMonitoringDashboardComponent,
                canActivate: [CanAccessPageGuard],
                data: {
                    requiredPermission: nameOf<OrganizationLevelPermissions>(
                        (x) => x.canAccessRealTimeDashboard,
                    ),
                },
            },
            {
                path: "zones",
                component: ZonesComponent,
                canActivate: [CanAccessPageGuard],
                data: {
                    requiredPermission: nameOf<OrganizationLevelPermissions>(
                        (x) => x.canAccessMappingModule,
                    ),
                },
            },
            {
                path: "workforce/shifts",
                component: ShiftListComponent,
                canActivate: [CanAccessPageGuard],
                data: {
                    requiredPermission: nameOf<OrganizationLevelPermissions>(
                        (x) => x.canAccessIhModule,
                    ),
                },
            },
            {
                path: "workforce/job-dictionary",
                component: JobDictionaryComponent,
                canActivate: [CanAccessPageGuard, EntityPermissionGuard],
                data: {
                    requiredPermission: nameOf<OrganizationLevelPermissions>(
                        (x) => x.canAccessIhModule,
                    ),
                    objectId: RolePermissionObjects.JobDictionary,
                },
            },
            {
                path: "workforce/job-mapping",
                component: JobMappingComponent,
                canActivate: [CanAccessPageGuard, EntityPermissionGuard],
                data: {
                    requiredPermission: nameOf<OrganizationLevelPermissions>(
                        (x) => x.canAccessIhModule,
                    ),
                    objectId: RolePermissionObjects.JobDictionaryMapping,
                },
            },
            {
                path: "workforce/:coId",
                component: EndpointsComponent,
                canActivate: [CanAccessPageGuard],
                data: {
                    requiredPermission: nameOf<OrganizationLevelPermissions>(
                        (x) => x.canAccessIhModule,
                    ),
                },
            },
            {
                path: "notification-settings",
                component: NotificationSettingsComponent,
                canActivate: [CanAccessPageGuard, EntityPermissionGuard],
                data: {
                    requiredPermission: nameOf<OrganizationLevelPermissions>(
                        (x) => x.canAccessRealTimeDashboard,
                    ),
                    objectId: RolePermissionObjects.NotificationSettings,
                },
            },
            {
                path: "realtime-config",
                canActivate: [CanAccessPageGuard],
                data: {
                    requiredPermission: nameOf<OrganizationLevelPermissions>(
                        (x) => x.canAccessRealTimeDashboard,
                    ),
                },
                children: [
                    {
                        path: "",
                        canActivate: [PermissionRedirectGuard],
                        component: PermissionRedirectPlaceholderComponent,
                    },
                    {
                        path: "parameters",
                        component: ParametersListComponent,
                        canActivate: [EntityPermissionGuard],
                        data: { objectId: RolePermissionObjects.Parameter },
                    },
                ],
            },
            {
                path: "notifications",
                component: NotificationListComponent,
                canActivate: [AdminGuard],
            },
            {
                path: "users",
                canActivate: [AdminGuard],
                children: [
                    { path: "", redirectTo: "management", pathMatch: "full" },
                    {
                        path: "management",
                        component: UserListComponent,
                        canActivate: [EntityPermissionGuard],
                        data: { objectId: RolePermissionObjects.User },
                    },
                    {
                        path: "roles",
                        component: RolePermissionsTabComponent,
                        canActivate: [EntityPermissionGuard],
                        data: { objectId: RolePermissionObjects.RolePermission },
                    },
                    {
                        path: "organizations",
                        component: OrganizationsComponent,
                    },
                ],
            },
            {
                path: "management",
                redirectTo: "users",
                pathMatch: "full",
            },
            {
                path: "reports",
                component: ReportListComponent,
                canActivate: [EntityPermissionGuard],
                data: { objectId: RolePermissionObjects.Report },
            },
            {
                path: "report/:id",
                component: ReportViewComponent,
            },
            {
                path: "unauthorized",
                component: UnauthorizedComponent,
            },
            {
                path: "integrations",
                canActivate: [CanAccessPageGuard],
                data: {
                    requiredPermission: nameOf<OrganizationLevelPermissions>(
                        (x) => x.canAccessIhModule,
                    ),
                },
                children: [
                    {
                        path: "",
                        redirectTo: "lab-integrations",
                        pathMatch: "full",
                    },
                    {
                        path: "lab-integrations",
                        component: LabIntegrationsComponent,
                    },
                    {
                        path: "lab-integrations/:id",
                        component: LabIntegrationDetailsComponent,
                    },
                ],
            },
            {
                path: "file-attachments",
                component: FileAttachmentListComponent,
                canActivate: [EntityPermissionGuard],
                data: { objectId: RolePermissionObjects.FileAttachment },
            },
            {
                path: "access-log",
                component: AccessLogListComponent,
                canActivate: [EntityPermissionGuard],
                data: { objectId: RolePermissionObjects.AccessLog },
            },
            {
                matcher: aiAnalystRouteMatcher,
                component: AiAnalystComponent,
                canActivate: [AiAnalystAccessGuard],
            },
            {
                path: "ai-analyst-admin",
                canActivate: [AdminOrSuperAdminGuard],
                children: [
                    { path: "", component: AiAnalystAdminComponent, pathMatch: "full", data: { section: "" } },
                    {
                        path: "enablement",
                        component: AiAnalystAdminComponent,
                        canActivate: [SuperAdminGuard],
                        data: { section: "enablement" },
                    },
                    {
                        path: "usage-by-user",
                        component: AiUsageByUserListComponent,
                        canActivate: [AiAnalystGuard, EntityPermissionGuard],
                        data: { objectId: RolePermissionObjects.AiUsageRecord },
                    },
                    {
                        path: "balances",
                        component: SiteCreditBalanceListComponent,
                        canActivate: [AiAnalystGuard, EntityPermissionGuard],
                        data: { objectId: RolePermissionObjects.SiteCreditBalance },
                    },
                    {
                        path: "success-metrics",
                        component: AiAnalystAdminComponent,
                        canActivate: [SuperAdminGuard],
                        data: { section: "success-metrics" },
                    },
                    { path: "credit-packages", redirectTo: "", pathMatch: "full" },
                    { path: "usage", redirectTo: "", pathMatch: "full" },
                ],
            },
            {
                path: "**",
                redirectTo: "my-dashboard",
                pathMatch: "full",
            },
        ],
    },
    {
        path: "userList",
        component: PreSignupUserListComponent,
        canActivate: [SuperAdminGuard, AuthGuard],
    },
    {
        path: "tenants",
        component: TenantListComponent,
        canActivate: [SuperAdminGuard, AuthGuard],
    },
    {
        path: "tenantSensorList",
        component: TenantSensorListComponent,
        canActivate: [SuperAdminGuard, AuthGuard],
    },
    {
        path: "shadowConstraintList",
        component: ShadowConstraintListComponent,
        canActivate: [SuperAdminGuard, AuthGuard],
    },
    {
        path: "deviceVersionList",
        component: DeviceVersionListComponent,
        canActivate: [SuperAdminGuard, AuthGuard],
    },
    {
        path: "dataAnalysis",
        component: DataAnalysisPageComponent,
        canActivate: [SuperAdminGuard, AuthGuard],
    },
    {
        path: "sensor",
        component: SensorComponent,
        canActivate: [SuperAdminGuard, AuthGuard],
    },
    {
        path: "**",
        redirectTo: "/dashboard",
        pathMatch: "full",
    },
];

@NgModule({
    imports: [
        RouterModule.forRoot(appRoutes, {
            // Don't perform initial navigation in iframes or popups
            // tslint:disable-next-line:max-line-length
            initialNavigation:
                !BrowserUtils.isInIframe() && !BrowserUtils.isInPopup()
                    ? "enabledNonBlocking"
                    : "disabled",
        }),
    ],
    exports: [RouterModule],
})
export class AppRoutingModule {}
